// apps/web/src/lib/api-client.ts
import { API_BASE } from './constants'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
  params?: Record<string, string | number | boolean | undefined>
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, params, ...rest } = opts

  let url = `${API_BASE}${path}`
  if (params) {
    const sp = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) sp.set(k, String(v))
    }
    const qs = sp.toString()
    if (qs) url += `?${qs}`
  }

  const res = await fetch(url, {
    ...rest,
    credentials: 'include', // send HttpOnly cookie automatically
    headers: {
      'Content-Type': 'application/json',
      ...rest.headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!res.ok) {
    let message = res.statusText
    let responseBody: unknown
    try {
      responseBody = await res.json()
      if (typeof responseBody === 'object' && responseBody !== null && 'message' in responseBody) {
        message = String((responseBody as { message: unknown }).message)
      }
    } catch {
      // non-JSON error body — keep statusText
    }
    throw new ApiError(res.status, message, responseBody)
  }

  if (res.status === 204) return undefined as T

  const json = await res.json()
  // Unwrap { data: ... } envelope if present
  if (json && typeof json === 'object' && 'data' in json) {
    return (json as { data: T }).data
  }
  return json as T
}

export const api = {
  get: <T>(path: string, params?: RequestOptions['params']) =>
    request<T>(path, { method: 'GET', params }),

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body }),

  delete: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),
}
